<template>
  <div class="disclosure">
    <h1 class="sr-only">Promo-disclosure</h1>
    <h2 class="headline1">Promotion Terms and Conditions</h2>
    <p class="primary-color">
      <strong>Effective as of September 2, 2026</strong>
    </p>
    <p>
      Electrify America, LLC (“Electrify America”) is pleased to provide this promotional charging program. By clicking
      “Apply this code” to confirm your submission of the promotional code that you entered in the mobile app, or by
      clicking “Confirm” to the promotional terms disclosed to you at checkout, you agree to these terms and conditions.
    </p>
    <p>
      Upon your confirmation of the promotional code and acceptance of these terms, you will receive a special discount
      on charging sessions initiated through the Electrify America app. The parameters of this discount vary depending
      on the type of promotion associated with each code, as indicated in the “Description” field of the “Verify promo
      code” screen of your app. This discount will continue until the expiration date specified in your app (“Expiration
      Date”), unless the promotion is earlier terminated pursuant to these terms and conditions.
    </p>
    <p>The different discount types are as follows:</p>
    <p>
      <strong>“One Month of Pass+ Free”</strong> – You will receive one free month of Pass+. Unless you cancel within
      one month of the start of your free trial, your Pass+ membership will automatically renew and you will be charged
      the then-current Pass+ price, beginning in one month and every month thereafter until you cancel. You can cancel
      your auto-renewing charges in the online Electrify America app before your renewal date. Offer is valid for a
      limited time. This promotion cannot be combined with any other coupons, discounts or promotional codes or offers
      and cannot be applied to a previous charge. Limit one (1) time use per customer. Offer is not valid for current
      Pass+ members or customers who have been Pass+ members within the last 90 days. This promotion is subject to these
      terms and Electrify America’s <router-link class="link" :to="{ name: 'terms-en' }">Terms of Use</router-link>. No
      cash value. Void where prohibited.
    </p>
    <p>
      <strong>“[$] off [#] sessions”</strong> - You will automatically receive the dollar amount discount on each
      session up to the specified number of charging sessions until the specified Expiration Date. Please note that each
      session to which the discount is applied will count toward the total number of sessions, even if the total cost of
      the session is less than the full discount amount.
    </p>
    <p>
      <strong>“[$] off all sessions until [date]”</strong> - You will automatically receive the dollar amount discount
      on all charging sessions until the specified Expiration Date.
    </p>
    <p>
      <strong>“[%] off [#] sessions”</strong> - The percentage discount will apply to each session up to the specified
      number of charging sessions until the specified Expiration Date. Please note that each session to which the
      discount is applied will count toward the total number of sessions.
    </p>
    <p>
      <strong>“[%] off all sessions until [date]”</strong> - The percentage discount will apply to all charging sessions
      until the specified Expiration Date.
    </p>
    <p>
      <strong>“[%] off first-time session until [date]”</strong> - The percentage discount will apply to Electrify
      America first-time customers until the specified Expiration Date.
    </p>
    <p>
      <strong>“[%] off one-time session until [date]”</strong> - Eligible customers selected by Electrify America may
      receive a randomly assigned discount off one (1) charging session at an Electrify America-owned charging station
      in the United States. Discount amounts vary by customer and will be communicated through the Electrify America app
      and/or applicable promotional materials. This promotion is available exclusively through the Electrify America
      app. To redeem the offer, the promotional code must be added to and activated in the Electrify America app before
      starting the charging session. The offer expires thirty (30) days after activation, and must be activated on or
      before March 31, 2027. Any offer not activated by March 31, 2027, is void. Offer details, including discount
      amounts, eligibility requirements, activation deadlines, and participating stations, may vary and will be
      communicated through the Electrify America app and/or applicable promotional materials. Limit one (1) discounted
      charging session per eligible customer. The offer is non-transferable, may not be combined with any other
      Electrify America promotion, discount, credit, rebate, incentive, or offer, is not redeemable for cash, and is
      void where prohibited by law. Electrify America reserves the right to modify, suspend, or terminate the promotion,
      in whole or in part, at any time. Participation in the promotion is subject to these terms and Electrify America's
      Terms of Use.
    </p>
    <p>
      By applying the promotional code to your account, you acknowledge and accept the above terms for the promotion
      type displayed in your app.
    </p>
    <p>
      You can receive the discount at any participating Electrify America charging station, exclusively by using your
      Electrify America plan. This discount may not be initiated using the interface screen or buttons on the charger.
    </p>
    <p>
      There is no additional fee, purchase, or subscription required for your access to this promotion, other than your
      continued subscription to the charging plan to which the promotional code has been applied.
    </p>
    <p>
      <strong>Limited-Time New Station Opening Promotion.</strong> Eligible customers may receive a percentage discount
      on charging sessions at select newly opened Electrify America charging stations. Discount amounts may vary by
      location. The promotion is available exclusively through the Electrify America app and may be redeemed only during
      the applicable promotional period. Unless otherwise specified in the Electrify America app and/or applicable
      promotion materials, the promotion period begins on the date the participating station first becomes available for
      public charging and ends at 11:59 p.m. local time on the seventh (7th) consecutive day thereafter. Promotional
      offers, discount amounts, eligibility requirements, participating stations, and other offer details may vary and
      will be communicated in the Electrify America app and/or applicable promotional materials. Offers are
      non-transferable, not redeemable for cash, and void where prohibited by law. Electrify America reserves the right
      to modify, suspend, or terminate the promotion, in whole or in part, at any time. Promotion is subject to these
      terms and Electrify America's Terms of Use.
    </p>
    <p class="primary-color">
      <strong>PLEASE NOTE THE FOLLOWING IMPORTANT LIMITATIONS ON YOUR USE OF THIS PROMOTION:</strong>
    </p>
    <p>
      This discount is applied to the net price of the charging session (including any idle fees incurred), before tax
      is calculated.
    </p>
    <p>
      You must follow all product, vehicle, safety, and technical documentation included with the vehicle when charging
      at an Electrify America charging station.
    </p>
    <p>
      Except as otherwise expressly agreed by Electrify America in writing, you may <strong>not</strong> use this
      discount in connection with any taxi, ride-share (e.g., Uber or Lyft), or other similar service, or for any other
      commercial purpose. <strong>This promotion is for your personal, non-commercial use only.</strong>
    </p>
    <p>
      Once this promotion has expired, you will not be able to resubmit, reactivate, or reapply the promotional code to
      your account. Please refer to the information on the <strong>“Plan Details”</strong> section of the app to keep
      track of the amount of time and/or charging sessions remaining in this promotion.
    </p>
    <p>
      These discounts are not returnable or redeemable for any cash value, and may not be sold, loaned, or otherwise
      distributed to any third party for any reason. For dollar amount discounts, if you do not use the full dollar
      value of the discount during a charging session, you will not be entitled to any cash or credit refund or
      disbursement of the difference, and you cannot apply unused discount amounts from one charging session to future
      charging sessions. You may not transfer any portion of the discounts to any other Electrify America account. Only
      one promotional code may be active for any plan at a given time, and discounts and offers cannot be combined.
    </p>
    <p>
      You acknowledge and agree that your use of Electrify America’s services and our relationship is subject to and
      governed by the Electrify America Terms of Use available at
      <router-link class="link" :to="{ name: 'terms-en' }">https://www.electrifyamerica.com/terms</router-link>,
      including the mandatory arbitration and class action waiver provisions, and that your use of the Electrify America
      mobile app is subject to the Terms of Use accessible by navigating to the <strong>Profile</strong> icon in top
      right hand corner of map, selecting <strong>“Legal”</strong> and then selecting
      <router-link class="medium" :to="{ name: 'terms-en' }">“Terms of Use”</router-link> &amp;
      <router-link class="medium" :to="{ name: 'privacy-en' }">“Privacy Policy.”</router-link> Please read these Terms
      of Use documents carefully for information about payments and other transactions related to each charging session.
    </p>
    <p>
      You agree to comply with all applicable laws and regulations when using Electrify America services, and when
      charging your vehicle at Electrify America charging stations.
    </p>
    <p>
      Electrify America reserves the right to withhold, revoke, reduce, terminate, or suspend your access to all or any
      portion of this promotion, without notice, if Electrify America determines or suspects, in its sole discretion,
      that you: (a) are in violation of the Electrify America Terms of Use, these Promotion Terms and Conditions, or any
      other contract between you and Electrify America; (b) have engaged in charging sessions that are excessive,
      fraudulent, or otherwise indicate an unauthorized or unintended use of this complimentary promotion; or (c) have
      engaged in any illegal, fraudulent, tortious, injurious, harmful, or abusive conduct in your use of the Electrify
      America services, the Electrify America mobile app, or an Electrify America charging station.
    </p>
    <p>
      Electrify America reserves the right to end this complimentary promotion at any time before its automatic
      termination date for any reason, in its sole discretion, provided that Electrify America will notify you by in-app
      notification, email, or other communication in the event of any change to the scheduled end date of this
      promotion. Following the termination or expiration of this program for any reason, your access to the
      complimentary promotion will immediately cease, and you may not use or redeem any further discounts.
    </p>
  </div>
</template>

<script>
export default {
  name: 'promo-disclosure',
  metaInfo: {
    title: 'Promo Disclosure  | Electrify America',
    link: [{ rel: 'canonical', href: 'https://www.electrifyamerica.com/promo-disclosure/' }],
  },
};
</script>
